<script>
import { layoutComputed } from "@/state/helpers";
import api from "@/services/http";

// Module-level cache: every page wraps its own <Layout>, so route navigation
// remounts this component. Without this cache, /api/menus would be hit on
// every page transition. Cleared on logout via clearMenuCache().
let menuCache = null;

export function clearMenuCache() {
  menuCache = null;
}

const FALLBACK_MENUS = [
  { id: -1, name: "Home", icon: "bx bx-home-alt", route: "/", children: [] },
  { id: -2, name: "Calendar", icon: "bx bx-calendar", route: "/work-sched/calendar", children: [] },
  { id: -3, name: "Analytics", icon: "bx bx-bar-chart-alt-2", route: "/work-sched/analytics", children: [] },
  { id: -4, name: "My Time Sheet", icon: "bx bx-spreadsheet", route: "/work-sched/time-sheet", children: [] },
  { id: -5, name: "My Daily Time Records", icon: "bx bx-time-five", route: "/work-sched/dtr", children: [] },
  { id: -6, name: "My Loan Ledger", icon: "bx bx-wallet", route: "/work-sched/loan-ledger", children: [] },
  { id: -7, name: "My Leave Ledger", icon: "bx bx-calendar-minus", route: "/work-sched/leave-ledger", children: [] },
  { id: -8, name: "My Payslip", icon: "bx bx-receipt", route: "/work-sched/payslip", children: [] },
  { id: -9, name: "Performance Evaluation", icon: "bx bx-line-chart", route: "/work-sched/performance", children: [] },
  {
    id: -10, name: "Filings", icon: "bx bx-edit-alt", route: null,
    children: [
      { id: -101, name: "Time Logs", icon: "bx bx-list-check", route: "/work-sched/transactions/time-logs" },
      { id: -102, name: "Change Shifts", icon: "bx bx-calendar-event", route: "/work-sched/transactions/change-shifts" },
      { id: -103, name: "Change Day Types", icon: "bx bx-calendar-edit", route: "/work-sched/transactions/change-day-types" },
      { id: -104, name: "Official Business", icon: "bx bx-briefcase-alt", route: "/work-sched/transactions/official-business" },
      { id: -105, name: "Leaves", icon: "bx bx-sun", route: "/work-sched/transactions/leaves" },
      { id: -106, name: "Overtime", icon: "bx bx-time-five", route: "/work-sched/transactions/overtime" },
      { id: -107, name: "My Requests", icon: "bx bx-user-voice", route: "/work-sched/transactions/my-requests" },
    ],
  },
  {
    id: -11, name: "Reports", icon: "bx bx-folder", route: null,
    children: [
      { id: -111, name: "Daily Time Records", icon: null, route: "/work-sched/reports/dtr" },
      { id: -112, name: "DTR — All Employees", icon: null, route: "/work-sched/reports/dtr-all" },
      { id: -113, name: "Tardiness & Lates", icon: null, route: "/work-sched/reports/tardiness" },
      { id: -114, name: "Absences", icon: null, route: "/work-sched/reports/absences" },
      { id: -115, name: "Overtime", icon: null, route: "/work-sched/reports/overtime" },
      { id: -116, name: "SSS Contribution", icon: null, route: "/work-sched/reports/sss" },
      { id: -117, name: "PhilHealth Contribution", icon: null, route: "/work-sched/reports/philhealth" },
      { id: -118, name: "Pag-IBIG Contribution", icon: null, route: "/work-sched/reports/pagibig" },
      { id: -119, name: "BIR Tax Withholding", icon: null, route: "/work-sched/reports/bir" },
    ],
  },
  {
    id: -12, name: "Administration", icon: "bx bx-shield", route: null,
    children: [
      { id: -121, name: "Audit Logs", icon: "bx bx-history", route: "/admin/audit-logs" },
      { id: -122, name: "DTR Records", icon: "bx bx-table", route: "/admin/dtr-list" },
      { id: -123, name: "Upload DTR", icon: "bx bx-cloud-upload", route: "/admin/dtr-upload" },
      { id: -124, name: "Biometric Upload", icon: "bx bx-fingerprint", route: "/admin/biometric-upload" },
    ],
  },
];

const MOBILE_HIDE_ROUTES = new Set(["/", "/work-sched/calendar", "/work-sched/analytics"]);

export default {
  data() {
    return {
      settings: { minScrollbarLength: 60 },
      menuItems: [],
      loadingMenus: true,
    };
  },
  computed: {
    ...layoutComputed,
    layoutType: {
      get() {
        return this.$store ? this.$store.state.layout.layoutType : {} || {};
      },
    },
  },

  watch: {
    $route: {
      handler: "onRoutechange",
      immediate: true,
      deep: true,
    },
  },

  async mounted() {
    await this.fetchMenus();
    this.$nextTick(() => this.bootstrapWiring());
  },

  methods: {
    async fetchMenus() {
      if (menuCache) {
        this.menuItems = menuCache;
        this.loadingMenus = false;
        return;
      }
      this.loadingMenus = true;
      try {
        const { data } = await api.get("/menus");
        const items = data?.data || [];
        menuCache = items.length ? items : FALLBACK_MENUS;
        this.menuItems = menuCache;
      } catch (e) {
        this.menuItems = FALLBACK_MENUS;
      } finally {
        this.loadingMenus = false;
      }
    },

    hideOnMobile(item) {
      return !!(item.route && MOBILE_HIDE_ROUTES.has(item.route));
    },

    bootstrapWiring() {
      this.initActiveMenu();
      if (this.rmenu == "vertical" && this.layoutType == "twocolumn") {
        document.documentElement.setAttribute("data-layout", "vertical");
      }
      const overlay = document.getElementById("overlay");
      if (overlay) {
        overlay.addEventListener("click", () => {
          document.body.classList.remove("vertical-sidebar-enable");
        });
      }

      window.addEventListener("resize", () => {
        if (this.layoutType == "twocolumn") {
          const windowSize = document.documentElement.clientWidth;
          if (windowSize < 767) {
            document.documentElement.setAttribute("data-layout", "vertical");
            this.rmenu = "vertical";
            localStorage.setItem("rmenu", "vertical");
          } else {
            document.documentElement.setAttribute("data-layout", "vertical");
            this.rmenu = "twocolumn";
            localStorage.setItem("rmenu", "twocolumn");
            setTimeout(() => this.initActiveMenu(), 50);
          }
        }
      });

      const collapses = document.querySelectorAll(".navbar-nav .collapse");
      collapses.forEach((collapse) => {
        collapse.addEventListener("show.bs.collapse", (e) => {
          e.stopPropagation();
          const closestCollapse = collapse.parentElement.closest(".collapse");
          if (closestCollapse) {
            const siblingCollapses = closestCollapse.querySelectorAll(".collapse");
            siblingCollapses.forEach((sib) => {
              if (sib.classList.contains("show")) {
                sib.classList.remove("show");
                sib.parentElement.firstElementChild.setAttribute("aria-expanded", "false");
              }
            });
          } else {
            const getSiblings = (elem) => {
              const siblings = [];
              let sibling = elem.parentNode.firstChild;
              while (sibling) {
                if (sibling.nodeType === 1 && sibling !== elem) siblings.push(sibling);
                sibling = sibling.nextSibling;
              }
              return siblings;
            };
            const siblings = getSiblings(collapse.parentElement);
            siblings.forEach((item) => {
              if (item.childNodes.length > 2) {
                item.firstElementChild.setAttribute("aria-expanded", "false");
                item.firstElementChild.classList.remove("active");
              }
              const ids = item.querySelectorAll("*[id]");
              ids.forEach((item1) => {
                item1.classList.remove("show");
                item1.parentElement.firstElementChild.setAttribute("aria-expanded", "false");
                item1.parentElement.firstElementChild.classList.remove("active");
                if (item1.childNodes.length > 2) {
                  const val = item1.querySelectorAll("ul li a");
                  val.forEach((subitem) => {
                    if (subitem.hasAttribute("aria-expanded"))
                      subitem.setAttribute("aria-expanded", "false");
                  });
                }
              });
            });
          }
        });

        collapse.addEventListener("hide.bs.collapse", (e) => {
          e.stopPropagation();
          const childCollapses = collapse.querySelectorAll(".collapse");
          childCollapses.forEach((c) => {
            c.classList.remove("show");
            c.parentElement.firstElementChild.setAttribute("aria-expanded", "false");
          });
        });
      });
    },

    onRoutechange(ele) {
      this.initActiveMenu(ele.path);
      if (document.getElementsByClassName("mm-active").length > 0) {
        const currentPosition = document.getElementsByClassName("mm-active")[0].offsetTop;
        if (currentPosition > 500 && this.$refs.isSimplebar) {
          this.$refs.isSimplebar.value.getScrollElement().scrollTop = currentPosition + 300;
        }
      }
    },

    initActiveMenu() {
      const pathName = window.location.pathname;
      const ul = document.getElementById("navbar-nav");
      if (ul) {
        const items = Array.from(ul.querySelectorAll("a.nav-link"));
        const activeItems = items.filter((x) => x.classList.contains("active"));
        this.removeActivation(activeItems);
        const matchingMenuItem = items.find((x) => x.getAttribute("href") === pathName);
        if (matchingMenuItem) {
          this.activateParentDropdown(matchingMenuItem);
        } else {
          const id = pathName.replace("/", "");
          if (id) document.body.classList.add("twocolumn-panel");
          this.activateIconSidebarActive(pathName);
        }
      }
    },

    removeActivation(items) {
      items.forEach((item) => {
        if (item.classList.contains("menu-link")) {
          if (!item.classList.contains("active")) item.setAttribute("aria-expanded", false);
          if (item.nextElementSibling) item.nextElementSibling.classList.remove("show");
        }
        if (item.classList.contains("nav-link")) {
          if (item.nextElementSibling) item.nextElementSibling.classList.remove("show");
          item.setAttribute("aria-expanded", false);
        }
        item.classList.remove("active");
      });
    },

    activateParentDropdown(item) {
      item.classList.add("active");
      const parentCollapseDiv = item.closest(".collapse.menu-dropdown");
      if (parentCollapseDiv) {
        parentCollapseDiv.classList.add("show");
        parentCollapseDiv.parentElement.children[0].classList.add("active");
        parentCollapseDiv.parentElement.children[0].setAttribute("aria-expanded", "true");
        this.activateIconSidebarActive("#" + parentCollapseDiv.getAttribute("id"));
        return false;
      }
      return false;
    },

    activateIconSidebarActive(id) {
      const menu = document.querySelector(
        "#two-column-menu .simplebar-content-wrapper a[href='" + id + "'].nav-icon"
      );
      if (menu !== null) menu.classList.add("active");
    },

    closeSidebar() {
      document.body.classList.remove("vertical-sidebar-enable");
      document.documentElement.setAttribute("data-sidebar-visibility", "hidden");
      if (window.innerWidth >= 992) {
        document.body.classList.add("ws-menu-hidden");
      }
    },
  },
};
</script>

<template>
  <BContainer fluid>
    <div id="two-column-menu"></div>

    <ul class="navbar-nav h-100" id="navbar-nav">
      <li class="menu-title ws-menu-title">
        <span>Menu</span>
        <button
          type="button"
          class="ws-menu-close"
          title="Close menu"
          @click="closeSidebar"
        >
          <i class="bx bx-menu"></i>
        </button>
      </li>

      <template v-if="loadingMenus">
        <li v-for="n in 8" :key="`skel-${n}`" class="nav-item ws-menu-skel">
          <div class="ws-menu-skel__row">
            <span class="ws-menu-skel__icon ws-shimmer"></span>
            <span class="ws-menu-skel__text ws-shimmer" :style="{ width: 60 + (n % 4) * 10 + '%' }"></span>
          </div>
        </li>
      </template>

      <li
        v-for="item in menuItems"
        v-else
        :key="item.id"
        class="nav-item"
        :class="{ 'ws-hide-mobile': hideOnMobile(item) }"
      >
        <!-- Parent (has children) -->
        <template v-if="item.children && item.children.length">
          <a
            class="nav-link menu-link"
            :href="`#sidebar-menu-${item.id}`"
            data-bs-toggle="collapse"
            role="button"
            aria-expanded="false"
            :aria-controls="`sidebar-menu-${item.id}`"
          >
            <i v-if="item.icon" :class="item.icon"></i>
            <span>{{ item.name }}</span>
          </a>
          <div class="collapse menu-dropdown" :id="`sidebar-menu-${item.id}`">
            <ul class="nav nav-sm flex-column">
              <li v-for="child in item.children" :key="child.id" class="nav-item">
                <router-link v-if="child.route" :to="child.route" class="nav-link">
                  {{ child.name }}
                </router-link>
                <span v-else class="nav-link text-muted">{{ child.name }}</span>
              </li>
            </ul>
          </div>
        </template>

        <!-- Leaf (route + no children) -->
        <router-link
          v-else-if="item.route"
          :to="item.route"
          class="nav-link menu-link"
        >
          <i v-if="item.icon" :class="item.icon"></i>
          <span>{{ item.name }}</span>
        </router-link>
      </li>
    </ul>
  </BContainer>
</template>

<style>
.menu-title.ws-menu-title {
  display: flex !important;
  align-items: center;
  justify-content: space-between;
  padding-right: 1rem;
  position: relative;
}
html[data-sidebar-size="sm"] .menu-title.ws-menu-title,
html[data-sidebar-size="sm-hover"] .menu-title.ws-menu-title,
html[data-sidebar-size="sm-hover-active"] .menu-title.ws-menu-title {
  justify-content: center;
  padding-right: 0;
}
html[data-sidebar-size="sm"] .menu-title.ws-menu-title > span,
html[data-sidebar-size="sm-hover"] .menu-title.ws-menu-title > span,
html[data-sidebar-size="sm-hover-active"] .menu-title.ws-menu-title > span {
  display: none !important;
}
.ws-menu-close {
  background: transparent;
  border: 0;
  padding: 0.25rem 0.5rem;
  font-size: 1.5rem;
  line-height: 1;
  color: var(--bs-body-color, #495057);
  cursor: pointer;
  display: inline-flex !important;
  align-items: center;
  visibility: visible !important;
  opacity: 1 !important;
}
.ws-menu-close i {
  display: inline-block !important;
  font-size: 1.5rem;
  line-height: 1;
}
.ws-menu-close:hover {
  color: var(--bs-primary);
}
@media (min-width: 992px) {
  .ws-menu-close { display: none !important; }
}
@media (max-width: 991.98px) {
  .ws-hide-mobile { display: none !important; }
}

/* Sidebar skeleton — shown while /api/menus is being fetched. Mirrors the
   nav-link layout so the loaded items don't make the sidebar jump. */
.ws-menu-skel { pointer-events: none; }
.ws-menu-skel__row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 1rem;
}
.ws-menu-skel__icon {
  width: 18px;
  height: 18px;
  border-radius: 4px;
  flex-shrink: 0;
}
.ws-menu-skel__text {
  height: 10px;
  border-radius: 4px;
}
.ws-shimmer {
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.18) 50%, rgba(255, 255, 255, 0.08) 100%);
  background-size: 200% 100%;
  animation: ws-shimmer-slide 1.4s ease-in-out infinite;
}
/* Light-themed sidebars use darker tints since the parent bg is light. */
html[data-sidebar="light"] .ws-shimmer,
html[data-bs-theme="light"] .ws-shimmer {
  background: linear-gradient(90deg, rgba(15, 23, 42, 0.08) 0%, rgba(15, 23, 42, 0.16) 50%, rgba(15, 23, 42, 0.08) 100%);
  background-size: 200% 100%;
}
@keyframes ws-shimmer-slide {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
</style>
